import { useMemo } from 'react'
import { useGetContractStatisticsQuery } from '../redux/api/contractsApi'
import { useGetAllCustomersQuery } from '../redux/api/customerApi'
import { ContactKind, CustomerIndexViewModel } from '@ail/customers-api'
import { Arr } from '../helpers/Arr'

interface CustomerFilter {
  manager?: string
  status?: string
}
interface StatsMapValue {
  key: string
  value: {
    [key: string]: number
  }
}

type StatsMap = Map<string, StatsMapValue[]>

export function useFilteredCustomers(filter?: CustomerFilter) {
  const customersNew = useGetAllCustomersQuery()
  const allStats = useGetContractStatisticsQuery({})

  const { statsMap } = useMemo(() => {
    const statsMap: StatsMap = new Map()

    if (allStats.data) {
      Object.entries(allStats.data).forEach(([key, value]) => {
        key = key.replace('$', '').trim()
        const [customerId] = key.split('//')
        if (!statsMap.has(customerId)) {
          statsMap.set(customerId, [])
        }
        statsMap.get(customerId)?.push({ key, value })
      })
    }
    return { statsMap }
  }, [allStats.data])

  const customersMap = Arr.toHashMap(
    customersNew.data ?? [],
    (c) => c.value,
    (c) => c.customer
  )
  const filteredCustomersByManager = useMemo(() => {
    let customers = customersNew.data || []
    if (!filter?.manager) return customers

    customers = filterByManager(customers, filter.manager)

    return customers
  }, [customersNew.data, filter?.manager])

  const filteredCustomers = useMemo(() => {
    if (!filter?.status) {
      return filteredCustomersByManager
    }

    return filterByStatus(filteredCustomersByManager, filter.status, statsMap)
  }, [filteredCustomersByManager, filter?.status, statsMap])

  const isFetching = allStats.isFetching || customersNew.isFetching
  return { customers: isFetching ? undefined : filteredCustomers, customersMap }
}

function filterByManager(customers: CustomerIndexViewModel[], selectedManager: string) {
  const [selectedManagerEmail, selectedManagerDisplayName] = selectedManager.split(' | ')
  return customers.filter((customerIndex) => {
    return customerIndex.customer?.managers.some((manager) => {
      const hasMatchingEmail = manager.contacts.some(
        (c) => c.kind === ContactKind.Email && c.value === selectedManagerEmail
      )
      const hasMatchingDisplayName = manager.displayName === selectedManagerDisplayName
      return hasMatchingEmail || hasMatchingDisplayName
    })
  })
}

function filterByStatus(customers: CustomerIndexViewModel[], statusFilter: string, statsMap: StatsMap) {
  return customers.filter((customerIndex) => {
    if (!customerIndex.value || !statsMap) return false
    if (statusFilter === 'ALL') return true

    const customerId = customerIndex.value.replace('$', '').trim()
    if (!customerId) {
      return false
    }
    const matchingStats = getMatchingStats(customerId, statsMap)
    if (!matchingStats.length) {
      return statusFilter === 'CANCELED'
    }

    if (statusFilter === 'ACTIVE') {
      return matchingStats.some(
        (stat) => stat.value.Running > 0 || stat.value.Initialized > 0 || stat.value.Extended > 0
      )
    } else if (statusFilter === 'CANCELED') {
      const hasActiveStates = matchingStats.some(
        (stat) => stat.value.Running > 0 || stat.value.Initialized > 0 || stat.value.Extended > 0
      )
      if (hasActiveStates) return false

      const hasCanceledStates = matchingStats.some(
        (stat) =>
          stat.value.Closed > 0 || stat.value.Terminated > 0 || stat.value.Canceled > 0 || stat.value.Ordered > 0
      )

      return hasCanceledStates
    }

    return true
  })
}

function getMatchingStats(customerId: string, statsMap: StatsMap) {
  const matchingStats = []
  if (statsMap.has(customerId)) {
    const customerStats = statsMap.get(customerId)
    if (customerStats) {
      matchingStats.push(...customerStats)
    }
  }
  return matchingStats
}
